import { useActionState, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AppHeader } from '@/components/dashboard/AppHeader'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { hasDisclaimerAcceptance } from '@/lib/disclaimer-access'
import { useSession } from '@/lib/session-context'

const FALLBACK_DESTINATION = '/'
const ACCEPTANCE_ERROR = 'Acceptance could not be recorded. Please try again.'

function safeReturnTo(location) {
  const candidate = location.state?.returnTo
  if (typeof candidate !== 'string') return FALLBACK_DESTINATION
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return FALLBACK_DESTINATION
  if (candidate.startsWith('/before-you-start')) return FALLBACK_DESTINATION

  return candidate
}

export function BeforeYouStartScreen() {
  const { acceptDisclaimer, currentUser } = useSession()
  const location = useLocation()
  const navigate = useNavigate()
  const [isAcknowledged, setIsAcknowledged] = useState(false)
  const disclaimer = currentUser?.disclaimer
  const returnTo = safeReturnTo(location)
  const [error, submitAction, isPending] = useActionState(async () => {
    if (!isAcknowledged) return null

    try {
      await acceptDisclaimer({ version: disclaimer.version })
      navigate(returnTo, { replace: true })
      return null
    } catch (submissionError) {
      if (submissionError?.status && submissionError.message) return submissionError.message

      return ACCEPTANCE_ERROR
    }
  }, null)

  if (!currentUser) return <Navigate to="/sign-in" replace />
  if (hasDisclaimerAcceptance(currentUser)) return <Navigate to={returnTo} replace />

  return (
    <main className="min-h-screen bg-background text-foreground">
      <title>Before you start — Sentinel OS</title>
      <AppHeader status="standby" />

      <section className="mx-auto flex w-full max-w-[900px] flex-col gap-8 p-4 md:p-8 lg:p-12">
        <Card>
          <CardHeader>
            <CardTitle><h1 className="text-3xl font-bold tracking-tight">{disclaimer.title}</h1></CardTitle>
            <CardDescription>
              Read and confirm before using Sentinel investment decision support.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6 text-sm leading-6 sm:text-base sm:leading-7">
            {disclaimer.introduction.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}

            <div className="space-y-3">
              <p className="font-medium">{disclaimer.due_diligence_lead}</p>
              <ul className="list-disc space-y-2 pl-6">
                {disclaimer.due_diligence_items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>

            <p>{disclaimer.closing}</p>

            <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
              <Checkbox
                id="disclaimer-acknowledgement"
                checked={isAcknowledged}
                onCheckedChange={(checked) => setIsAcknowledged(checked === true)}
                className="mt-1"
              />
              <label
                htmlFor="disclaimer-acknowledgement"
                className="cursor-pointer text-sm leading-6"
              >
                {disclaimer.acknowledgement}
              </label>
            </div>

            <form action={submitAction} className="space-y-5">

              {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}

              <Button type="submit" size="lg" disabled={!isAcknowledged || isPending}>
                {isPending ? 'Recording acceptance...' : 'Accept and continue'}
              </Button>
            </form>
          </CardContent>

          <CardFooter>
            <p className="text-xs text-muted-foreground">
              Acceptance applies to current disclaimer version.
            </p>
          </CardFooter>
        </Card>
      </section>
    </main>
  )
}
